<section class="completion-section">
  <div class="container">
    <div class="completion-card">
      <!-- Success Icon -->
      <div class="success-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>

      <!-- Title -->
      <h1 class="completion-title">{{ "page.questionnaire.completed.title" | translate }}</h1>

      <!-- Two Column Layout -->
      <div class="results-container">
        <!-- Left Column: Score & Interpretation -->
        <div class="left-column">
          <!-- Score Display -->
          <div class="score-card" *ngIf="score !== null && score !== undefined">
            <div class="score-details">
              <p class="score-text">
                {{ "page.questionnaire.completed.score.text" | translate: { score: score } }}
              </p>
              <p class="score-range" *ngIf="interpretationRange">
                {{ "page.questionnaire.completed.score.range" | translate: { range: interpretationRange } }}
              </p>
              <p class="risk-level" *ngIf="interpretation">
                {{ "page.questionnaire.completed.riskLevel" | translate }}
                <span class="risk-badge">{{ interpretation.result | translationValue }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Right Column: Next Steps & Actions -->
        <div class="right-column">
          <!-- Next Steps Display -->
          <div class="next-steps-section" *ngIf="availableServiceRequests.length > 0 || (nextSteps && nextSteps.length > 0)">
            <!-- DNA -->
            <div *ngIf="dna">
              <h3 class="next-steps-title">
                {{
                  (dna.hasDNA ? "page.questionnaire.completed.dnaRequiredExisting" : "page.questionnaire.completed.dnaRequiredNew")
                    | translate
                }}
              </h3>

              <!-- DNA Service Request with Checkboxes -->
              <div *ngIf="dna" class="next-step-item service-request-item">
                <label class="service-checkbox">
                  <input type="checkbox" [checked]="dnaSelected" (change)="dnaSelected = !dnaSelected" />
                  <span class="checkmark"></span>
                  <div class="service-content">
                    <p class="service-message">
                      {{ dna.message | translationValue }}
                      <span *ngIf="dna.latestDate">
                        ({{ "page.questionnaire.completed.decodedOn" | translate }} {{ dna.latestDate | localizedDate: "dd MMMM yyyy" }})
                      </span>
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <!-- Service Requests with Checkboxes -->
            <div *ngIf="availableServiceRequests.length > 0">
              <h3 class="next-steps-title">{{ "page.questionnaire.completed.recommended" | translate }}</h3>

              <!-- Service Requests with Checkboxes -->
              <div *ngFor="let serviceRequest of availableServiceRequests" class="next-step-item service-request-item">
                <label class="service-checkbox">
                  <input
                    type="checkbox"
                    [checked]="isServiceRequestSelected(serviceRequest.value)"
                    (change)="toggleServiceRequest(serviceRequest.value)"
                  />
                  <span class="checkmark"></span>
                  <div class="service-content">
                    <p class="service-message">{{ serviceRequest.message | translationValue }}</p>
                  </div>
                </label>
              </div>
            </div>

            <!-- Other recommendations -->
            <div *ngIf="nextSteps && hasNonServiceRequestSteps">
              <h3 class="next-steps-title">{{ "page.questionnaire.completed.otherRecommendations" | translate }}</h3>

              <!-- Other Non-Service Request Messages -->
              <ng-container *ngFor="let step of nextSteps">
                <!-- Skip service requests as they're handled above -->
                <div *ngIf="step.resultType !== RuleResultType.SERVICE_REQUEST" class="next-step-item">
                  <!-- Regular Message -->
                  <div *ngIf="step.messageType === MessageType.MESSAGE" class="step-message">
                    <p>{{ step.message | translationValue }}</p>
                  </div>

                  <!-- HTML Message -->
                  <div
                    *ngIf="step.messageType === MessageType.HTML"
                    class="step-message"
                    [innerHTML]="step.message | translationValue"
                  ></div>

                  <!-- Knowledge/Media Message -->
                  <div *ngIf="step.messageType === MessageType.MEDIA" class="step-knowledge">
                    <mat-expansion-panel>
                      <mat-expansion-panel-header>
                        <mat-panel-title> Learn More </mat-panel-title>
                      </mat-expansion-panel-header>
                      <div class="knowledge-content" *ngIf="!isLoadingRiskDetails && riskDetails.knowledge">
                        <ng-container *ngFor="let media of riskDetails.knowledge.medias">
                          <h4>{{ media.label }}</h4>
                          <div [innerHTML]="media.description"></div>
                        </ng-container>
                      </div>
                      <div class="knowledge-content" *ngIf="isLoadingRiskDetails">
                        <mat-spinner diameter="30"></mat-spinner>
                        <p>{{ "page.questionnaire.completed.loadingInfo" | translate }}</p>
                      </div>
                    </mat-expansion-panel>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Order Options (Service Request) -->
          <div class="order-section" *ngIf="showOrderButtons">
            <button
              class="btn-primary-large"
              (click)="addServiceRequestToCart(false)"
              [disabled]="isAddingToCart || (selectedServiceRequests.size === 0 && !this.dnaSelected)"
            >
              <span *ngIf="!isAddingToCart">
                {{ "page.questionnaire.completed.addToCart" | translate }}
              </span>
              <span *ngIf="isAddingToCart">{{ "page.questionnaire.completed.adding" | translate }}</span>
            </button>

            <button
              class="btn-primary-large"
              (click)="addServiceRequestToCart(true)"
              [disabled]="isAddingToCart || (selectedServiceRequests.size === 0 && !this.dnaSelected)"
            >
              {{ "page.questionnaire.completed.orderNow" | translate }}
            </button>
          </div>

          <!-- No Next Steps Display -->
          <div class="no-next-steps-section" *ngIf="!nextSteps || nextSteps.length === 0">
            <p>{{ "page.questionnaire.completed.noNextSteps" | translate }}</p>
          </div>

          <!-- Progress Info -->
          <div class="progress-info" *ngIf="allQuestionnaires.length > 1">
            <p class="progress-text">
              {{ "page.questionnaire.completed.progressText" | translate: { current: currentIndex + 1, total: allQuestionnaires.length } }}
            </p>
            <div class="progress-dots">
              <span
                *ngFor="let q of allQuestionnaires; let i = index"
                class="dot"
                [class.completed]="i <= currentIndex"
                [class.current]="i === currentIndex"
              >
              </span>
            </div>
          </div>

          <!-- Continue to Next Questionnaire -->
          <div class="continue-section" *ngIf="hasNext">
            <button class="btn-primary-large" (click)="startNextQuestionnaire()">
              {{ "page.questionnaire.completed.continueNext" | translate }} →
            </button>
          </div>

          <!-- All Completed Message -->
          <div class="success-box" *ngIf="!hasNext && !showOrderButtons && allQuestionnaires.length > 0">
            <p class="success-text">✓ {{ "page.questionnaire.completed.allCompleted" | translate }}</p>
          </div>
        </div>
      </div>

      <!-- Navigation Options (Middle) -->
      <div class="navigation-options">
        <div class="risk-knowledge-section">
          <div class="knowledge-content" *ngIf="!isLoadingRiskDetails && riskDetails.knowledge">
            <ng-container *ngFor="let media of riskDetails.knowledge.medias">
              <h4>{{ media.label }}</h4>
              <div [innerHTML]="media.description"></div>
            </ng-container>
          </div>
          <div class="knowledge-content" *ngIf="isLoadingRiskDetails">
            <mat-spinner diameter="30"></mat-spinner>
            <p>{{ "page.questionnaire.completed.loadingInfo" | translate }}</p>
          </div>
        </div>
        <div class="navigation-buttons">
          <button class="btn-link" [routerLink]="['/risks', riskId]">{{ "button.learnMore" | translate }} →</button>
          <button class="btn-link" [routerLink]="sessionService.isLoggedIn ? '/home' : '/landing'">
            {{ (sessionService.isLoggedIn ? "common.returnHome" : "common.returnLanding") | translate }}
          </button>
        </div>
      </div>

      <!-- Questions in current slide (Bottom) -->
      <div class="question-block" *ngIf="slideViews.length > 0">
        <ng-container *ngFor="let slideView of slideViews">
          <ng-container *ngFor="let questionView of slideView.questions">
            <app-encode-question
              *ngIf="isQuestionEnabled(questionView.question)"
              [viewAnswerOnly]="true"
              [question]="questionView.question"
              [response]="questionView.response"
              [choices]="getChoices(questionView.question)"
            ></app-encode-question>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </div>
</section>
